import { type Action, stringify } from '@0xtorch/core'
import type { AssetBalance } from '../../types/assetBalance'
import type { AssetBorrow } from '../../types/assetBorrow'
import type { CostBasis } from '../../types/costBasis'
import type { CrossActionBundle } from '../../types/crossActionBundle'
import type { Loan } from '../../types/loan'
import type {
  NftAcquiredTimestamps,
  Transaction,
} from '../../types/transaction'
import { checkIfTransactionIsValid } from '../../utils/checkIfTransactionIsValid'
import { updateNftAcquiredTimestampsByTransaction } from '../utils/transactionTransferPl'
import { createTransactionsByBorrowAction } from './createTransactionsByBorrowAction'
import { createTransactionsByCrossReplaceAction } from './createTransactionsByCrossReplaceAction'
import { createTransactionsByCrossTradeAction } from './createTransactionsByCrossTradeAction'
import { createTransactionsByDepositAction } from './createTransactionsByDepositAction'
import { createTransactionsByFeeAction } from './createTransactionsByFeeAction'
import { createTransactionsByIgnoreAction } from './createTransactionsByIgnoreAction'
import { createTransactionsByIncomeAction } from './createTransactionsByIncomeAction'
import { createTransactionsByLiquidationAction } from './createTransactionsByLiquidationAction'
import { createTransactionsByRepaymentAction } from './createTransactionsByRepaymentAction'
import { createTransactionsByReplaceAction } from './createTransactionsByReplaceAction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import { createTransactionsByTransferAction } from './createTransactionsByTransferAction'
import { createTransactionsByValuedownAction } from './createTransactionsByValuedownAction'
import { createTransactionsByValueupAction } from './createTransactionsByValueupAction'
import { createTransactionsByWithdrawAction } from './createTransactionsByWithdrawAction'

type CreateTransactionsParameters = {
  readonly actionId: string
  readonly action: Action
  readonly loans: Map<string, Loan>
  readonly crossActionBundles: Map<string, CrossActionBundle>
  readonly assetBalances: Map<string, readonly AssetBalance[]>
  readonly assetBorrows: Map<string, readonly AssetBorrow[]>
  readonly usedBalances: Map<string, AssetBalance>
  readonly nftAcquiredTimestamps?: NftAcquiredTimestamps
  readonly costBasis: CostBasis
  readonly isLocked?: boolean
  readonly handleLoanAsTrade?: boolean
  readonly handleReplaceAsTrade?: boolean
  readonly handleTransferAsLoss?: boolean
  readonly use5PercentRule?: boolean
}

type CreateTransactionsReturnTypes = {
  readonly transactions: readonly Transaction[]
  readonly loans: Map<string, Loan>
  readonly crossActionBundles: Map<string, CrossActionBundle>
  readonly assetBalances: Map<string, readonly AssetBalance[]>
  readonly assetBorrows: Map<string, readonly AssetBorrow[]>
  readonly usedBalances: Map<string, AssetBalance>
  readonly nftAcquiredTimestamps: NftAcquiredTimestamps
}

type CreateTransactionsInternalReturnTypes = Omit<
  CreateTransactionsReturnTypes,
  'nftAcquiredTimestamps'
>

/**
 * Action から会計用 transaction 群を生成し、共通の整形を適用します
 * @param parameters transaction 生成に必要な action と各種状態
 * @returns 生成された transactions と更新後の状態
 */
export const createTransactions = ({
  action,
  ...parameters
}: CreateTransactionsParameters): CreateTransactionsReturnTypes => {
  const result = createTransactionsInternal({
    action,
    ...parameters,
  })
  const transactions: Transaction[] = []
  let nftAcquiredTimestamps =
    parameters.nftAcquiredTimestamps ?? new Map<string, number>()
  for (const [index, transaction] of result.transactions.entries()) {
    if (!checkIfTransactionIsValid(transaction)) {
      throw new Error(`Transaction is not valid: ${stringify(transaction)}`)
    }
    transaction.order = index
    if (action.subType !== undefined) {
      transaction.subType = action.subType
    }
    nftAcquiredTimestamps = updateNftAcquiredTimestampsByTransaction({
      transaction,
      nftAcquiredTimestamps,
    })
    transactions.push(transaction)
  }
  return {
    ...result,
    transactions,
    nftAcquiredTimestamps,
  }
}

const createTransactionsInternal = (
  parameters: CreateTransactionsParameters,
): CreateTransactionsInternalReturnTypes => {
  const { action } = parameters
  switch (action.type) {
    case 'add-liquidity': {
      return createTransactionsByDepositAction({
        ...parameters,
        borrowSubCategory: 'bond',
      })
    }
    case 'airdrop': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'airdrop',
      })
    }
    case 'approve': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'atomic-arbitrage': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'borrow': {
      return createTransactionsByBorrowAction({
        ...parameters,
      })
    }
    case 'bridge-from': {
      return createTransactionsByCrossReplaceAction({
        ...parameters,
        feeSubCategory: 'bridge-fee',
        replaceSubCategory: 'bridge',
      })
    }
    case 'bridge-to': {
      return createTransactionsByCrossReplaceAction({
        ...parameters,
        feeSubCategory: 'bridge-fee',
        replaceSubCategory: 'bridge',
      })
    }
    case 'burn': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'burn',
      })
    }
    case 'buy-crypto': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'buy-nft': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'cash-back': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'cash-back',
      })
    }
    case 'cross-replace': {
      return createTransactionsByCrossReplaceAction({
        ...parameters,
      })
    }
    case 'cross-trade': {
      return createTransactionsByCrossTradeAction({
        ...parameters,
      })
    }
    case 'deposit': {
      return createTransactionsByDepositAction({
        ...parameters,
        borrowSubCategory: 'bond',
      })
    }
    case 'deposit-with-bond': {
      return createTransactionsByDepositAction({
        ...parameters,
        borrowSubCategory: 'bond',
      })
    }
    case 'derivative-fee': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'derivative-fee',
      })
    }
    case 'derivative-profit': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'derivative-profit',
      })
    }
    case 'fail-tx': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'fee': {
      return createTransactionsByFeeAction({
        ...parameters,
      })
    }
    case 'free-mint': {
      return createTransactionsByIncomeAction({
        ...parameters,
      })
    }
    case 'game-reward': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'game-reward',
      })
    }
    case 'gift': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'gift',
      })
    }
    case 'hardfork': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'hardfork',
      })
    }
    case 'ignore': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'income': {
      return createTransactionsByIncomeAction({
        ...parameters,
      })
    }
    case 'interest': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'interest',
      })
    }
    case 'interest-fee': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'interest-fee',
      })
    }
    case 'liquidation': {
      return createTransactionsByLiquidationAction({
        ...parameters,
      })
    }
    case 'mining': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'mining',
      })
    }
    case 'mint-nft': {
      return createTransactionsByTradeAction({
        ...parameters,
        tradeSubCategory: 'mint',
      })
    }
    case 'move': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'nft-royalty': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'nft-royalty',
      })
    }
    case 'pay': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'pay',
      })
    }
    case 'realize-loss': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'realize-loss',
      })
    }
    case 'realize-profit': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'realize-profit',
      })
    }
    case 'receive-from-cex': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'remove-liquidity': {
      return createTransactionsByWithdrawAction({
        ...parameters,
      })
    }
    case 'repayment': {
      return createTransactionsByRepaymentAction({
        ...parameters,
      })
    }
    case 'replace': {
      return createTransactionsByReplaceAction({
        ...parameters,
      })
    }
    case 'sell-crypto': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'revoke': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'sell-nft': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'send-to-cex': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'spam': {
      return createTransactionsByIgnoreAction({
        ...parameters,
      })
    }
    case 'stake': {
      return createTransactionsByDepositAction({
        ...parameters,
      })
    }
    case 'staking-reward': {
      return createTransactionsByIncomeAction({
        ...parameters,
        incomeSubCategory: 'staking-reward',
      })
    }
    case 'stolen': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'stolen',
      })
    }
    case 'swap-nft': {
      return createTransactionsByReplaceAction({
        ...parameters,
      })
    }
    case 'trade': {
      return createTransactionsByTradeAction({
        ...parameters,
      })
    }
    case 'transaction-fee': {
      return createTransactionsByFeeAction({
        ...parameters,
        feeSubCategory: 'transaction-fee',
      })
    }
    case 'transfer': {
      return createTransactionsByTransferAction({
        ...parameters,
      })
    }
    case 'unstake': {
      return createTransactionsByWithdrawAction({
        ...parameters,
      })
    }
    case 'unwrap': {
      return createTransactionsByReplaceAction({
        ...parameters,
      })
    }
    case 'value-down': {
      return createTransactionsByValuedownAction({
        ...parameters,
      })
    }
    case 'value-up': {
      return createTransactionsByValueupAction({
        ...parameters,
      })
    }
    case 'withdraw': {
      return createTransactionsByWithdrawAction({
        ...parameters,
      })
    }
    case 'withdraw-with-bond': {
      return createTransactionsByWithdrawAction({
        ...parameters,
      })
    }
    case 'wrap': {
      return createTransactionsByReplaceAction({
        ...parameters,
      })
    }
    default: {
      throw new Error(`Unknown action type: ${action.type satisfies never}`)
    }
  }
}
